import { ButtonAlert } from 'components/ButtonAlert';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { CostGroupEnum } from 'gql/graphql';
import { ActKc2StatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { getActKC2StatusLabel } from 'pages/Project/Common/types';
import type { Position } from 'uikit';
import { IconTrash, Tooltip } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';
import { getElementCoordinatesBySelector } from 'utils/dom';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { gqlDeleteActKC2 } from '../workOrderQueries';

interface IActKC2DeleteProps {
    actKC2Id?: number;
    status?: ActKc2StatusEnum;
    number?: number | string | null;
    date?: Date | null;
    buttonNode?: any;
    hasNoButton?: boolean;
    isDisabled?: boolean;
    onDelete?: () => void;
    costGroup?: CostGroupEnum;
    withCloseHelper?: (action: () => void) => () => void;
}

export const ActKC2Delete: React.FC<IActKC2DeleteProps> = (props) => {
    const dispatch = useDispatch();

    const { id: projectId, orderId } = useParams();
    const { projectData } = useProjectData(projectId, false);
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const {
        actKC2Id,
        number,
        date,
        buttonNode,
        hasNoButton,
        onDelete,
        status,
        isDisabled,
        costGroup,
        withCloseHelper,
    } = props;
    const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<Position>(undefined);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const variables = useMemo(() => ({ actKC2Id }), [actKC2Id]);

    const [deleteActKC2Data, deleteActKC2Status, deleteActKC2Errors, deleteActKC2] = useRequest({
        requestId: `deleteActKC2${actKC2Id}`,
        query: gqlDeleteActKC2,
        withEffect: false,
        variables,
    });

    const { aktKC2: aktKC2Rules } = useAccess({ statusKC2: status });

    const onDialogClose = useCallback(() => {
        if (withCloseHelper) {
            withCloseHelper(() => setConfirmationDialogOpen(false))();
        } else {
            setConfirmationDialogOpen(false);
        }
    }, [withCloseHelper]);

    const onDeleteConfirmed = useCallback(() => {
        setConfirmationDialogOpen(false);
        setIsSubmitted(true);
        dispatch(deleteActKC2(variables));
    }, [deleteActKC2, dispatch, variables]);

    const onDeleteClick = useCallback(() => {
        setConfirmationDialogOpen(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_act KC delete button_pressed',
            projectId,
            orderId: orderId ? Number(orderId) : undefined,
            costGroup,
            actKC2Status: status,
            actKC2Id,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectDO: projectData?.subsidiary?.name,
            projectType: projectData?.contractType,
        });
    }, [
        actKC2Id,
        costGroup,
        orderId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        status,
    ]);

    useEffect(() => {
        if (deleteActKC2Status === RequestStatus.SUCCESS && deleteActKC2Data?.uerDeleteActKC2?.result && isSubmitted) {
            onDelete?.();
            setIsSubmitted(false);
        }
    }, [deleteActKC2Status, deleteActKC2Data?.uerDeleteActKC2?.result, onDelete, isSubmitted]);

    useEffect(() => {
        buttonNode?.addEventListener('click', onDeleteClick);

        return () => buttonNode?.removeEventListener('click', onDeleteClick);
    }, [buttonNode, onDeleteClick]);

    const isAktInProgress = status === ActKc2StatusEnum.InProgress;
    const isDisabledBtn = !isAktInProgress || (isAktInProgress && !aktKC2Rules.canDelete);

    const onMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const { target } = e;
        const position = getElementCoordinatesBySelector(target, 'button', true);

        setTooltipPosition(position);
    }, []);

    const onMouseLeave = useCallback(() => {
        setTooltipPosition(undefined);
    }, []);

    const onCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const onCreateReport = () => {
        ExportCSVReport(deleteActKC2Data?.uerDeleteActKC2?.validation);
    };

    return (
        <>
            {!hasNoButton && (
                <div
                    style={{ display: 'inline' }}
                    onMouseLeave={isDisabledBtn ? onMouseLeave : undefined}
                    onMouseEnter={isDisabledBtn ? onMouseEnter : undefined}
                >
                    <ButtonAlert
                        disabled={isDisabledBtn || isDisabled}
                        onlyIcon
                        iconRight={IconTrash}
                        iconSize="s"
                        size="s"
                        onClick={onDeleteClick}
                    />
                </div>
            )}
            {isDisabledBtn && (
                <Tooltip size="m" direction="upCenter" position={tooltipPosition}>
                    <div style={{ color: '#EEF8FB' }}>
                        {isAktInProgress && !aktKC2Rules.canDelete ? (
                            `У вас недостаточно прав для удаления Актa${descriptionNumberAndDate(number, date)}`
                        ) : (
                            <span>
                                Невозможно удаление Акта <br /> в статусе «{getActKC2StatusLabel(status)}»
                            </span>
                        )}
                    </div>
                </Tooltip>
            )}
            <ConfirmationDialog
                textHeader="УДАЛЕНИЕ АКТА"
                textConfirmation={`Вы уверены, что хотите удалить ${formatActTitle(number, date as Date)}?`}
                cancelButtonText="Отменить"
                confirmButtonText="Удалить"
                isOpened={isConfirmationDialogOpen}
                onSubmit={onDeleteConfirmed}
                onClose={onDialogClose}
            />
            <ErrorHandlingModal
                titleText="Удаление акта КС-2"
                isSubmitted={isSubmitted}
                statusRequest={deleteActKC2Status}
                dataRequest={deleteActKC2Data?.uerDeleteActKC2}
                errorsRequest={deleteActKC2Errors}
                handleCloseModalError={onCloseModalError}
                handleCreateReport={onCreateReport}
            />
        </>
    );
};

ActKC2Delete.displayName = 'ActKC2Delete';
